import { LoaderFunctionArgs, redirect } from 'react-router-dom'
import { store } from '@/app/store'
import { CheckInSetting, fetchCheckInSetting } from '@/features/hotel/check-in-setting-slice'
import { AccountInfo } from '@/features/user/account-slice'
import { globalHotelId } from '@/hooks/use-hotel-id'

export const IdentityVerifyPath = '/checkin-identity-verify'

export const IdentityVerifyDefaultNextPath = '/checkin-confirm'

/**
 * **このゲストが本人確認の対象か**（＝本人確認のページに入るか）。
 *
 * 施設が事前チェックインで本人確認を行う設定であることに加えて、
 * **ゲスト自身の在住地（`accountInfo.nationality`。`JPN` = 日本在住 /
 * `NJP` = 海外在住）がその施設の対象に含まれていること**を見る。
 * 「日本在住は対象外／海外在住は対象」の施設で、日本在住のゲストに
 * 本人確認のページを出さないための判断はここ。
 *
 * 在住地が判定できないとき（アカウント情報が未取得）は**入る側に倒す**。
 * 誤ってスキップすると本人確認そのものが行われなくなるため。
 */
export const requiresIdentityVerifyForGuest = (checkInSetting?: CheckInSetting, nationality?: AccountInfo['nationality']): boolean => {
  if (checkInSetting?.requiredPreCheckinIdentify !== 1) {
    return false
  }
  const forJapanese = checkInSetting.requiredIdentify === 1
  const forOverseas = checkInSetting.requiredForeignIdentify === 1
  if (!nationality) {
    return forJapanese || forOverseas
  }
  return nationality === 'JPN' ? forJapanese : forOverseas
}

/**
 * 保護対象のページ（`/checkin-confirm` / `/checkin-payment`）に入る前に本人確認を挟む loader。
 *
 * `checkIn.identification.hasFinishedIdentityVerify` が立っていれば何もしない。
 * ここで「必要か」だけを見ると、本人確認を終えて確認画面へ進んだ瞬間にまた
 * このloaderに捕まってループするため、**済んだ印を先に見る。**
 * `/checkin-identity-verify` 自身はこの loader の対象にしない（ループの原因になる）。
 */
/** 本人確認の後に戻ってよい行き先。`nextPath` は URL に載る値なので、ここに無いものは受け取らない */
const NextPathAllowList = ['/checkin-confirm', '/checkin-payment']

/**
 * `nextPath` を行き先として使ってよいか確かめる。
 *
 * **絶対 URL を `redirect` に渡すと、react-router が別オリジンへページごと遷移させる**
 * （`@remix-run/router` の `startRedirectNavigation`）。URL を作る側が自由に決められる値なので、
 * 許した 2 つのパスだけを通し、それ以外は既定の行き先に倒す。
 */
export const safeNextPath = (nextPath: string | null | undefined): string => {
  if (!nextPath) {
    return IdentityVerifyDefaultNextPath
  }
  const pathname = nextPath.split(/[?#]/)[0]
  return NextPathAllowList.includes(pathname) ? nextPath : IdentityVerifyDefaultNextPath
}

/**
 * 本人確認はこのチェックインのためのものなので、チェックインの施設 ID を使う。
 * `globalHotelId` は URL のクエリで上書きされるため、別施設のリンクを開くと
 * その施設の設定でスキップの可否が決まり、画像も別施設の置き場へ上がる
 */
const identityVerifyHotelId = (): string => store.getState().checkIn.checkInData.fields.hotelId || globalHotelId()

const fetchLatestCheckInSetting = async (hotelId: string): Promise<CheckInSetting | undefined> => {
  // `fetchCheckInSetting` は取得中だと `condition` で弾かれて空のまま返るため、
  // 他画面の取得と重なっていても `isForce` で取り直す
  const action = await store.dispatch(fetchCheckInSetting({ hotelId, isForce: true }))
  // 取得に失敗しても rejected の action が返るだけで例外にならない。
  // slice は施設 ID を持たないため、失敗したときに読むと前の施設の設定を使ってしまう
  if (!fetchCheckInSetting.fulfilled.match(action)) {
    return undefined
  }
  return store.getState().checkInSetting.checkInSetting
}

/**
 * 本人確認のページに入る前に施設設定を揃える。ページ側は store を読むだけにする。
 *
 * 取れなかったときは本人確認を出さずに次のページへ送る（`requireIdentityVerify` が
 * 設定を取れないときに通すのと同じ扱い）。ここで待たせると、設定が無いままでは
 * 出せる書類もスキップの可否も決まらず、読み込み中から抜けられなくなる。
 */
export const loadIdentityVerifySetting = async ({ request }: LoaderFunctionArgs) => {
  const hotelId = identityVerifyHotelId()
  const checkInSetting = hotelId ? await fetchLatestCheckInSetting(hotelId) : undefined
  if (checkInSetting) {
    return null
  }
  return redirect(safeNextPath(new URL(request.url).searchParams.get('nextPath')))
}

export const requireIdentityVerify = async ({ request }: LoaderFunctionArgs) => {
  if (store.getState().checkIn.identification.hasFinishedIdentityVerify) {
    return null
  }

  const hotelId = identityVerifyHotelId()
  if (!hotelId) {
    return null
  }

  const checkInSetting = await fetchLatestCheckInSetting(hotelId)
  const nationality = store.getState().accountInfo.user?.nationality

  // 設定が取れなかったときは通す。この先には現地のセルフチェックインがあり、
  // 本人確認が済んでいなければそこで行うため、ここで止める必要がない
  if (!requiresIdentityVerifyForGuest(checkInSetting, nationality)) {
    return null
  }

  const url = new URL(request.url)
  const nextPath = `${url.pathname}${url.search}`
  const params = new URLSearchParams({ hotelId, nextPath })
  return redirect(`${IdentityVerifyPath}?${params.toString()}`)
}
